<template>
    <div>
        <div v-if="loading" class="rr-loading"><v-progress-circular indeterminate color="primary" size="26" width="3" /></div>
        <template v-else>
            <div v-for="block in blocks" :key="block.key" class="rr-sources-block">
                <div v-if="blocks.length > 1" class="rr-trend-title">
                    <span v-if="block.logo" class="rr-platform-logo" v-html="block.logo"></span>
                    {{ block.label }}
                </div>
                <table v-if="block.rows.length" class="rr-table">
                    <thead>
                        <tr>
                            <th>Domain</th>
                            <th>Coverage</th>
                            <th>Responses</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in block.rows" :key="block.key + row.domain">
                            <td class="rr-brand-cell">
                                <img class="rr-favicon" :src="faviconUrl(row.domain)" alt="" @error="$event.target.style.display = 'none'" />
                                {{ row.domain }}
                            </td>
                            <td>
                                <span class="rr-bar rr-bar--inline"><span class="rr-bar-fill" :style="{ width: Math.min(100, row.coverage || 0) + '%' }"></span></span>
                                {{ row.coverage != null ? row.coverage.toFixed(1) + "%" : "—" }}
                                <span v-if="row.coverageDelta != null" class="rr-delta-chip" :class="{ up: row.coverageDelta > 0, down: row.coverageDelta < 0 }">
                                    <v-icon x-small>{{ row.coverageDelta > 0 ? "mdi-arrow-up" : row.coverageDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                                    {{ (row.coverageDelta > 0 ? "+" : "") + row.coverageDelta.toFixed(1) }}%
                                </span>
                            </td>
                            <td>
                                {{ row.responsesCount }}
                                <span v-if="row.responsesDelta != null" class="rr-delta-chip" :class="{ up: row.responsesDelta > 0, down: row.responsesDelta < 0 }">
                                    <v-icon x-small>{{ row.responsesDelta > 0 ? "mdi-arrow-up" : row.responsesDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                                    {{ (row.responsesDelta > 0 ? "+" : "") + row.responsesDelta }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-else class="rr-empty">No sources found for the selected filters.</div>
            </div>
        </template>
    </div>
</template>

<script>
    import { fetchSources, faviconUrl, platformSnapshotDates } from "../reportApi"
    import { getPlatformMeta } from "../../../utils/platformMeta"

    export default {
        name: "SourcesSection",
        props: {
            filters: { type: Object, required: true },
            options: { type: Object, default: () => ({}) },
            companyId: { type: String, required: true },
            projectId: { type: String, required: true },
            project: { type: Object, default: null },
            platformKeys: { type: Array, default: () => [] }
        },
        data() {
            return { loading: true, blocks: [] }
        },
        methods: {
            faviconUrl,
            // Merge old/new domain lists into display rows with coverage + deltas.
            buildRows(oldRows, newRows, divisorOld, divisorNew) {
                const norm = (d) => (d || "").toLowerCase()
                const oldMap = new Map(oldRows.map((r) => [norm(r.domain), r]))
                const fallbackDivisor = (this.platformKeys.length || 1) * (this.project?.keyword_count || 0) || null
                const dNew = divisorNew || fallbackDivisor
                const dOld = divisorOld || fallbackDivisor
                return newRows
                    .slice()
                    .sort((a, b) => (b.responsesCount || 0) - (a.responsesCount || 0))
                    .slice(0, 10)
                    .map((r) => {
                        const old = oldMap.get(norm(r.domain))
                        const coverage = dNew ? ((r.responsesCount || 0) / dNew) * 100 : null
                        const coverageOld = old && dOld ? ((old.responsesCount || 0) / dOld) * 100 : null
                        return {
                            domain: r.domain,
                            responsesCount: r.responsesCount || 0,
                            responsesDelta: old ? (r.responsesCount || 0) - (old.responsesCount || 0) : null,
                            coverage,
                            coverageDelta: coverage != null && coverageOld != null ? coverage - coverageOld : null
                        }
                    })
            },
            async fetchScope(platformKeysForScope) {
                // per-platform snapshot timestamps: backends match importDate exactly
                const entries = platformKeysForScope
                    .map((p) => ({ platform: p, range: platformSnapshotDates(this.project, this.filters.selectedDates, p) }))
                    .filter((e) => e.range)
                    .map((e) => ({ platform: e.platform, importDates: e.range.start === e.range.end ? [e.range.end] : [e.range.start, e.range.end] }))
                if (!entries.length) return []
                const payload = {
                    project_id: this.projectId,
                    platform: null,
                    importDatesByPlatform: entries,
                    sort: { responsesCount: -1 },
                    skip: 0,
                    limit: 0
                }
                if (this.filters.tags && this.filters.tags.length) {
                    payload.tags = this.filters.tags
                    payload.tagsOperator = this.filters.tagsOperator
                }
                const data = await fetchSources(this.companyId, payload)
                const byDate = Array.isArray(data.sourcesByDate) ? data.sourcesByDate : []
                const totals = Array.isArray(data.totalResponsesByDate) ? data.totalResponsesByDate : []
                const oldRows = Array.isArray(byDate[0]) ? byDate[0] : []
                const newRows = Array.isArray(byDate[1]) && byDate[1].length ? byDate[1] : oldRows
                const compare = Array.isArray(byDate[1]) && byDate[1].length ? oldRows : []
                return this.buildRows(compare, newRows, totals[0] || null, totals[1] || totals[0] || null)
            },
            async fetchAll() {
                this.loading = true
                try {
                    if (this.options.per_platform && this.platformKeys.length > 1) {
                        const results = await Promise.all(this.platformKeys.map((key) => this.fetchScope([key]).catch(() => [])))
                        this.blocks = this.platformKeys.map((key, i) => {
                            const meta = getPlatformMeta(key)
                            return { key, label: meta.label || key, logo: meta.logo, rows: results[i] }
                        })
                    } else {
                        const rows = await this.fetchScope(this.platformKeys)
                        this.blocks = [{ key: "all", label: "All Platforms", logo: null, rows }]
                    }
                } catch (e) {
                    this.blocks = [{ key: "all", label: "All Platforms", logo: null, rows: [] }]
                } finally {
                    this.loading = false
                    this.$emit("ready")
                }
            }
        },
        mounted() {
            this.fetchAll()
        }
    }
</script>

<style scoped>
    .rr-sources-block {
        margin-bottom: 18px;
    }
</style>
